import React, { useEffect, useMemo, useState } from 'react'
import { PlusIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { Button, Heading, Input, LoadingSpinner, Text } from '../../components/ui'
import EstoqueItemCombobox from '../Estoque/EstoqueItemCombobox'
import { estoqueService } from '../../services/estoque'
import { marketingSolicitacaoService } from '../../services/marketingSolicitacao'
import type { EstoqueFornecedor, EstoqueItem } from '../../types/estoque'
import type { MarketingRequest } from '../../types/marketingSolicitacao'
import type {
  SupplierQuote,
  SupplierQuoteInput,
  SupplierQuoteItemInput,
  SupplierQuoteStatus,
} from '../../types/supplierQuotes'
import SolicitacaoPickerField from './SolicitacaoPickerField'
import {
  SELECT_CLASS,
  STATUS_LABELS,
  TEXTAREA_CLASS,
  emptyQuoteItem,
  formatCurrency,
  itemsFromRequest,
} from './propostasUi'

type Props = {
  open: boolean
  editing: SupplierQuote | null
  busy?: boolean
  onClose: () => void
  onSave: (input: SupplierQuoteInput) => void | Promise<void>
}

const PropostasFormModal: React.FC<Props> = ({
  open,
  editing,
  busy = false,
  onClose,
  onSave,
}) => {
  const [supplierId, setSupplierId] = useState('')
  const [requestId, setRequestId] = useState('')
  const [title, setTitle] = useState('')
  const [status, setStatus] = useState<SupplierQuoteStatus>('rascunho')
  const [freightValue, setFreightValue] = useState(0)
  const [discountValue, setDiscountValue] = useState(0)
  const [validUntil, setValidUntil] = useState('')
  const [deliveryDeadline, setDeliveryDeadline] = useState('')
  const [notes, setNotes] = useState('')
  const [items, setItems] = useState<SupplierQuoteItemInput[]>([emptyQuoteItem()])

  const [fornecedores, setFornecedores] = useState<EstoqueFornecedor[]>([])
  const [stockItems, setStockItems] = useState<EstoqueItem[]>([])
  const [requests, setRequests] = useState<MarketingRequest[]>([])
  const [loadingMeta, setLoadingMeta] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lastAppliedRequestId, setLastAppliedRequestId] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    let cancelled = false

    const load = async () => {
      setLoadingMeta(true)
      setError(null)
      try {
        const [fornecedoresData, stockData, requestsData] = await Promise.all([
          estoqueService.listFornecedores(false),
          estoqueService.listItens(false),
          marketingSolicitacaoService.listRequests(),
        ])
        if (cancelled) return
        setFornecedores(fornecedoresData.filter((f) => f.is_active))
        setStockItems(stockData.filter((i) => i.is_active))
        setRequests(requestsData)

        if (editing) {
          setSupplierId(editing.supplier_id || '')
          setRequestId(editing.request_id || '')
          setTitle(editing.title || '')
          setStatus(editing.status)
          setFreightValue(Number(editing.freight_value || 0))
          setDiscountValue(Number(editing.discount_value || 0))
          setValidUntil(editing.valid_until?.slice(0, 10) || '')
          setDeliveryDeadline(editing.delivery_deadline?.slice(0, 10) || '')
          setNotes(editing.notes || '')
          setItems(
            (editing.items || []).map((item) => ({
              stock_item_id: item.stock_item_id,
              request_item_id: item.request_item_id,
              description: item.description,
              quantity: Number(item.quantity),
              unit: item.unit,
              unit_value: Number(item.unit_value),
              sort_order: item.sort_order,
            }))
          )
          setLastAppliedRequestId(editing.request_id || null)
        } else {
          setSupplierId('')
          setRequestId('')
          setTitle('')
          setStatus('rascunho')
          setFreightValue(0)
          setDiscountValue(0)
          setValidUntil('')
          setDeliveryDeadline('')
          setNotes('')
          setItems([emptyQuoteItem()])
          setLastAppliedRequestId(null)
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Erro ao carregar formulário.')
        }
      } finally {
        if (!cancelled) setLoadingMeta(false)
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [open, editing])

  const itemsSubtotal = useMemo(
    () =>
      items.reduce(
        (acc, item) => acc + Number(item.quantity || 0) * Number(item.unit_value || 0),
        0
      ),
    [items]
  )
  const total =
    Math.round((itemsSubtotal + Number(freightValue || 0) - Number(discountValue || 0)) * 10000) /
    10000

  const applyRequest = (nextRequestId: string) => {
    setRequestId(nextRequestId)
    if (!nextRequestId) {
      setLastAppliedRequestId(null)
      return
    }
    if (editing && lastAppliedRequestId === nextRequestId) return
    const request = requests.find((r) => r.id === nextRequestId)
    if (!request) return
    if (!title.trim()) {
      setTitle(request.title?.trim() || `Solicitação ${request.request_number}`)
    }
    if (request.needed_on) {
      setDeliveryDeadline(request.needed_on.slice(0, 10))
    }
    setItems(itemsFromRequest(request))
    setLastAppliedRequestId(nextRequestId)
  }

  const updateItem = (index: number, patch: Partial<SupplierQuoteItemInput>) => {
    setItems((prev) =>
      prev.map((item, i) => {
        if (i !== index) return item
        return { ...item, ...patch }
      })
    )
  }

  const handleSave = async () => {
    setError(null)
    if (!supplierId) {
      setError('Selecione o fornecedor.')
      return
    }
    if (!title.trim()) {
      setError('Informe o título da proposta.')
      return
    }
    if (!items.length) {
      setError('Informe ao menos um item.')
      return
    }
    try {
      await onSave({
        supplier_id: supplierId,
        request_id: requestId || null,
        title: title.trim(),
        status,
        freight_value: Number(freightValue || 0),
        discount_value: Number(discountValue || 0),
        valid_until: validUntil || null,
        delivery_deadline: deliveryDeadline || null,
        notes: notes.trim() || null,
        items: items.map((item, index) => ({
          ...item,
          sort_order: index * 10,
          quantity: Number(item.quantity),
          unit_value: Number(item.unit_value),
        })),
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar proposta.')
    }
  }

  if (!open) return null

  const locked = editing?.status === 'nota_gerada'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={busy ? undefined : onClose} />
      <div className="relative z-10 flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl bg-white shadow-xl dark:bg-gray-800">
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-5 py-4 dark:border-gray-700">
          <div>
            <Heading as="h3" size="xl" className="text-gray-900 dark:text-white">
              {editing ? 'Editar proposta' : 'Nova proposta'}
            </Heading>
            <Text size="sm" color="muted" className="mt-1">
              Orçamento de um fornecedor, com ou sem solicitação vinculada.
            </Text>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loadingMeta ? (
            <div className="flex justify-center py-16">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="space-y-4">
              {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200">
                  {error}
                </div>
              )}
              {locked && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
                  Proposta com nota gerada. Apenas visualização.
                </div>
              )}

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Fornecedor *
                  </label>
                  <select
                    className={SELECT_CLASS + ' w-full'}
                    value={supplierId}
                    disabled={locked || busy}
                    onChange={(e) => setSupplierId(e.target.value)}
                  >
                    <option value="">Selecione o fornecedor</option>
                    {fornecedores.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Status
                  </label>
                  <select
                    className={SELECT_CLASS + ' w-full'}
                    value={status}
                    disabled={locked || busy || status === 'nota_gerada'}
                    onChange={(e) => setStatus(e.target.value as SupplierQuoteStatus)}
                  >
                    {(Object.keys(STATUS_LABELS) as SupplierQuoteStatus[])
                      .filter((key) => key !== 'nota_gerada' || status === 'nota_gerada')
                      .map((key) => (
                        <option key={key} value={key}>
                          {STATUS_LABELS[key]}
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              <SolicitacaoPickerField
                label="Solicitação"
                requests={requests}
                value={requestId}
                disabled={locked || busy}
                onChange={applyRequest}
              />

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Título *
                </label>
                <Input
                  value={title}
                  disabled={locked || busy}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Frete
                  </label>
                  <Input
                    type="number"
                    min={0}
                    step="any"
                    value={freightValue}
                    disabled={locked || busy}
                    onChange={(e) =>
                      setFreightValue(e.target.value === '' ? 0 : Number(e.target.value))
                    }
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Desconto
                  </label>
                  <Input
                    type="number"
                    min={0}
                    step="any"
                    value={discountValue}
                    disabled={locked || busy}
                    onChange={(e) =>
                      setDiscountValue(e.target.value === '' ? 0 : Number(e.target.value))
                    }
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Validade
                  </label>
                  <Input
                    type="date"
                    value={validUntil}
                    disabled={locked || busy}
                    onChange={(e) => setValidUntil(e.target.value)}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Prazo de entrega
                  </label>
                  <Input
                    type="date"
                    value={deliveryDeadline}
                    disabled={locked || busy}
                    onChange={(e) => setDeliveryDeadline(e.target.value)}
                  />
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Observações
                </label>
                <textarea
                  className={TEXTAREA_CLASS}
                  rows={3}
                  value={notes}
                  disabled={locked || busy}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Text className="font-medium text-gray-900 dark:text-white">Itens</Text>
                  {!locked && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setItems((prev) => [...prev, emptyQuoteItem()])}
                    >
                      <PlusIcon className="h-4 w-4" />
                      Item
                    </Button>
                  )}
                </div>

                {items.map((item, index) => (
                  <div
                    key={index}
                    className="grid grid-cols-1 gap-2 rounded-lg border border-gray-200 p-3 dark:border-gray-700 lg:grid-cols-12"
                  >
                    <div className="lg:col-span-4">
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                        Item de estoque
                      </label>
                      <EstoqueItemCombobox
                        items={stockItems}
                        value={item.stock_item_id || ''}
                        disabled={locked || busy}
                        onChange={(id) => {
                          const stock = stockItems.find((s) => s.id === id)
                          updateItem(index, {
                            stock_item_id: id,
                            description: item.description || stock?.name || '',
                            unit: stock?.unit || item.unit || '',
                          })
                        }}
                      />
                    </div>
                    <div className="lg:col-span-3">
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                        Descrição
                      </label>
                      <Input
                        value={item.description || ''}
                        disabled={locked || busy}
                        onChange={(e) => updateItem(index, { description: e.target.value })}
                      />
                    </div>
                    <div className="lg:col-span-1">
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                        Qtd
                      </label>
                      <Input
                        type="number"
                        min={0}
                        step="any"
                        value={item.quantity}
                        disabled={locked || busy}
                        onChange={(e) =>
                          updateItem(index, {
                            quantity: e.target.value === '' ? 0 : Number(e.target.value),
                          })
                        }
                      />
                    </div>
                    <div className="lg:col-span-2">
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                        Preço unit.
                      </label>
                      <Input
                        type="number"
                        min={0}
                        step="any"
                        value={item.unit_value}
                        disabled={locked || busy}
                        onChange={(e) =>
                          updateItem(index, {
                            unit_value: e.target.value === '' ? 0 : Number(e.target.value),
                          })
                        }
                      />
                    </div>
                    <div className="flex items-end justify-between gap-2 lg:col-span-2">
                      <Text size="sm" className="font-medium">
                        {formatCurrency(
                          Number(item.quantity || 0) * Number(item.unit_value || 0)
                        )}
                      </Text>
                      {!locked && items.length > 1 && (
                        <button
                          type="button"
                          className="rounded p-1.5 text-gray-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
                          onClick={() => setItems((prev) => prev.filter((_, i) => i !== index))}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-end">
                <Text className="text-lg font-semibold text-gray-900 dark:text-white">
                  Total: {formatCurrency(total)}
                </Text>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-4 dark:border-gray-700">
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          {!locked && (
            <Button type="button" onClick={() => void handleSave()} disabled={busy || loadingMeta}>
              {busy ? 'Salvando...' : 'Salvar'}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

export default PropostasFormModal
